import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Play, Lock, BookOpen, CheckCircle2, Youtube } from "lucide-react";

// Extract YouTube video ID from any URL format (watch?v=, youtu.be/, embed/)
function ytEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    let id = null;
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (u.searchParams.get("v")) id = u.searchParams.get("v");
    else if (u.pathname.includes("/embed/")) id = u.pathname.split("/embed/")[1];
    if (!id) return null;
    return `https://www.youtube.com/embed/${id.split("?")[0].split("&")[0]}`;
  } catch { return null; }
}

const LEVELS = [
  { key: "quiz", label: "Niveau 1", time: 30, color: "#1368ce" },
  { key: "difficile", label: "Niveau 2", time: 25, color: "#d89e00" },
  { key: "legend", label: "Légende", time: 15, color: "#e21b3c" },
];

export default function CourseDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [levels, setLevels] = useState(null);

  useEffect(() => {
    http.get("/courses").then((r) => {
      setAllCourses(r.data);
      const c = r.data.find((x) => x.id === cid);
      if (!c) { toast.error("Cours introuvable"); nav("/app/courses"); return; }
      setCourse(c);
    });
  }, [cid, nav]);

  useEffect(() => {
    if (!course || user.role !== "eleve") return;
    http.get(`/courses/${cid}/levels`).then((r) => setLevels(r.data)).catch(() => {});
  }, [course, cid, user.role]);

  if (!course) return <div className="text-[#5b6577]">Chargement…</div>;

  const canAccess = course.unlocked || user.role !== "eleve";
  const prev = allCourses.filter(c => c.order < course.order).sort((a,b)=>b.order-a.order)[0];
  const next = allCourses.filter(c => c.order > course.order).sort((a,b)=>a.order-b.order)[0];

  return (
    <div className="max-w-4xl mx-auto" data-testid="course-detail-page">
      <Link to="/app/courses" className="inline-flex items-center gap-2 text-[#5b6577] hover:text-[#00a86b] text-sm font-headings font-semibold mb-5" data-testid="course-back">
        <ArrowLeft className="w-4 h-4" /> Retour au catalogue
      </Link>

      <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 md:p-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-mono text-[#9aa5bc]">{`C${course.competency}-${String(course.order).padStart(2, "0")}`} · Compétence {course.competency}</div>
            <h1 className="mt-2 font-headings font-black text-3xl md:text-4xl text-[#1a2332] tracking-tight">{course.title}</h1>
            {course.key_points && <p className="mt-3 text-sm text-[#5b6577] italic">{course.key_points}</p>}
          </div>
          {canAccess ? (
            <span className="inline-flex items-center gap-1.5 bg-[#00a86b]/10 text-[#00a86b] rounded-full px-3 py-1 text-xs font-headings font-bold"><BookOpen className="w-3.5 h-3.5"/> Accessible</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 bg-[#f3f5fa] text-[#5b6577] rounded-full px-3 py-1 text-xs font-headings font-bold"><Lock className="w-3.5 h-3.5"/> Verrouillé</span>
          )}
        </div>

        <article
          className="mt-8 prose prose-sm md:prose-base max-w-none text-[#1a2332] whitespace-pre-wrap leading-relaxed"
          data-testid="course-content"
        >
          {canAccess ? (course.content || "Contenu pédagogique à venir.") :
            "Ce cours n'est pas encore débloqué par votre mentor. Demandez-lui un déblocage pour accéder au contenu."}
        </article>

        {canAccess && course.video_url && ytEmbedUrl(course.video_url) && (
          <div className="mt-10 border-t border-black/5 pt-8" data-testid="course-video">
            <div className="flex items-center gap-2 font-headings font-black text-xl text-[#1a2332] mb-4">
              <Youtube className="w-5 h-5 text-[#e21b3c]" /> Vidéo pédagogique
            </div>
            <div className="relative w-full overflow-hidden rounded-2xl shadow-lg" style={{ paddingBottom: "56.25%" }}>
              <iframe
                src={ytEmbedUrl(course.video_url)}
                title={`Vidéo — ${course.title}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
                data-testid="course-video-iframe"
              />
            </div>
          </div>
        )}

        {canAccess && course.has_quiz && (
          <div className="mt-10 border-t border-black/5 pt-8">
            <div className="font-headings font-black text-xl text-[#1a2332] mb-4">Tester ses connaissances</div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
              {LEVELS.map((l) => {
                const st = levels?.[l.key];
                const isLocked = user.role === "eleve" && st && !st.unlocked;
                const isPassed = st?.passed;
                if (isLocked) {
                  return (
                    <div key={l.key} data-testid={`detail-quiz-${l.key}-locked`}
                      className="relative rounded-lg px-3 py-3 text-sm font-headings font-bold bg-[#f3f5fa] border border-black/5 text-[#9aa5bc] flex items-center justify-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" /> {l.label}
                    </div>
                  );
                }
                return (
                  <Link key={l.key} to={`/app/courses/${cid}/quiz/${l.key}`}
                    data-testid={`detail-quiz-${l.key}`}
                    className="relative rounded-lg px-3 py-3 text-sm font-headings font-bold text-white hover:opacity-90 flex items-center justify-center gap-2 shadow-sm"
                    style={{ backgroundColor: l.color }}>
                    {isPassed && <CheckCircle2 className="w-3.5 h-3.5" />}
                    <Play className="w-3.5 h-3.5" /> {l.label} · {l.time}s
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        {prev ? (
          <Link to={`/app/courses/${prev.id}`} data-testid="course-prev" className="bg-white border border-black/5 rounded-2xl p-4 hover:shadow-md transition-shadow">
            <div className="text-xs text-[#9aa5bc]">← Précédent</div>
            <div className="font-headings font-bold text-[#1a2332] mt-1">{prev.title}</div>
          </Link>
        ) : <div />}
        {next ? (
          <Link to={`/app/courses/${next.id}`} data-testid="course-next" className="bg-white border border-black/5 rounded-2xl p-4 hover:shadow-md transition-shadow text-right">
            <div className="text-xs text-[#9aa5bc]">Suivant →</div>
            <div className="font-headings font-bold text-[#1a2332] mt-1">{next.title}</div>
          </Link>
        ) : <div />}
      </div>
    </div>
  );
}
